import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { buildRoomPackageOptionsViewOrder } from 'lib/offer/offerPageUtil'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import AccommodationRoomTile from 'components/OfferPage/Accommodation/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/OfferPage/Accommodation/AccommodationRoomTileImage'
import AlwaysOnAboutRoomModalTab from './AlwaysOnAboutRoomModalTab'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import zIndex from 'styles/tools/z-index'
import AlwaysOnPackageOptionsModalTab from './AlwaysOnPackageOptionsModalTab'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import config from 'constants/config'
import SinglePropertyRoomAndPackageDetailsModal from './SinglePropertyRoomAndPackageDetailsModal'
import AccommodationMobileHeaderFilters from 'components/OfferPage/Accommodation/AccommodationMobileHeaderFilters'

const StickyTabButtonsGroup = styled(TabButtonsGroup)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  offer: App.Offer;
  room: App.PackageRoomType;
  packageOptions: Array<OfferPackageOptionView>;
  onClose?: () => void;
}

function AlwaysOnRoomAndPackageDetailsModal(props: Props) {
  const {
    offer,
    room,
    packageOptions,
    onClose,
  } = props

  const [propertyMapping] = usePropertyOfferMapping(offer.property?.id, {
    disabled: !config.ACCOMM_USE_LPC_PROPERTY_PAGE,
  })

  const [selectedTab, setSelectedTab] = useState<'package-options' | 'about-room'>('package-options')
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  const packageViews = !propertyMapping ? packageOptionsByRoom.find(option => option.roomId === room.id)?.packageViews : []
  const pkg = !propertyMapping ? (packageViews?.[0].package as App.HotelPackage) : null

  const selectPackageOptionsTab = useCallback(() => {
    setSelectedTab('package-options')
  }, [])

  const selectAboutRoomTab = useCallback(() => {
    setSelectedTab('about-room')
  }, [])

  if (propertyMapping) {
    return <SinglePropertyRoomAndPackageDetailsModal
      onClose={onClose}
    />
  }

  if (!pkg || !packageViews?.length) return null

  return (<>
    <ModalHeader
      title={room.name}
      dismissible={false}
      onBackButtonClick={onClose}
    >
      <AccommodationMobileHeaderFilters />
    </ModalHeader>
    <ModalBody>
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage roomPackage={pkg} />
      </AccommodationRoomTile>
      <StickyTabButtonsGroup fit="grow">
        <TabButton size="medium" isActive={selectedTab === 'package-options'} onClick={selectPackageOptionsTab}>
          Package options
        </TabButton>
        <TabButton size="medium" isActive={selectedTab === 'about-room'} onClick={selectAboutRoomTab}>
          About the room
        </TabButton>
      </StickyTabButtonsGroup>
      {selectedTab === 'package-options' && <AlwaysOnPackageOptionsModalTab packageViews={packageViews} />}
      {selectedTab === 'about-room' && <AlwaysOnAboutRoomModalTab pkg={pkg} />}
    </ModalBody>
  </>)
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  offer: state.offer,
  room: state.room,
  packageOptions: getDurationPackageOptions(state, appState),
}), AlwaysOnRoomAndPackageDetailsModal)
